import { useState, useEffect } from 'react'
import { DocumentArrowUpIcon } from '@heroicons/react/24/outline'
import { uploadResume, analyzeResume } from '../services/resumeService'
import { saveResumeConsent } from '../services/settingsRepo'
import { UpgradePrompt } from './UpgradePrompt'
import { DataProcessingConsentModal } from './DataProcessingConsentModal'
import type { ResumeAnalysis } from '../types'

interface Props {
  userId: string
  onAnalysisComplete: (analysis: ResumeAnalysis | null) => void
  currentAnalysis: ResumeAnalysis | null
  canAnalyzeNew: boolean
  hasConsent: boolean
  onConsentGiven: () => void
}

const ANALYZING_MESSAGES = [
  'Analyzing resume...',
  'Extracting text content...',
  'Checking ATS compatibility...',
  'Evaluating structure and format...',
  'Assessing strengths...',
  'Identifying areas for improvement...',
  'Analyzing experience section...',
  'Reviewing skills and keywords...',
  'Generating actionable suggestions...',
  'Scanning for missing keywords...',
  'Evaluating formatting consistency...',
  'Finalizing analysis...',
]

export function ResumeAnalyzer({ userId, onAnalysisComplete, currentAnalysis, canAnalyzeNew, hasConsent, onConsentGiven }: Props) {
  const [uploading, setUploading] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dragActive, setDragActive] = useState(false)
  const [analyzingMessageIndex, setAnalyzingMessageIndex] = useState(0)
  const [showConsentModal, setShowConsentModal] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)

  // Rotate analyzing messages
  useEffect(() => {
    if (!analyzing) {
      setAnalyzingMessageIndex(0)
      return
    }

    const interval = setInterval(() => {
      setAnalyzingMessageIndex((prev) => (prev + 1) % ANALYZING_MESSAGES.length)
    }, 3500)

    return () => clearInterval(interval)
  }, [analyzing])

  const handleFile = async (file: File) => {
    // Check if user has given consent
    if (!hasConsent) {
      setPendingFile(file)
      setShowConsentModal(true)
      return
    }

    setError(null)
    setUploading(true)

    try {
      // Upload file
      const filePath = await uploadResume(file, userId)

      setUploading(false)
      setAnalyzing(true)

      // Analyze
      const analysis = await analyzeResume(filePath)

      setAnalyzing(false)
      onAnalysisComplete(analysis)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed')
      setUploading(false)
      setAnalyzing(false)
    }
  }

  const handleConsent = async () => {
    try {
      await saveResumeConsent(userId)
      onConsentGiven()
      setShowConsentModal(false)
      
      // Process pending file if exists
      if (pendingFile) {
        const file = pendingFile
        setPendingFile(null)
        
        // Start upload and analysis directly
        setError(null)
        setUploading(true)

        try {
          const filePath = await uploadResume(file, userId)
          setUploading(false)
          setAnalyzing(true)

          const analysis = await analyzeResume(filePath)
          setAnalyzing(false)
          onAnalysisComplete(analysis)
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Analysis failed')
          setUploading(false)
          setAnalyzing(false)
        }
      }
    } catch {
      setError('Failed to save consent')
    }
  }

  const handleDeclineConsent = () => {
    setShowConsentModal(false)
    setPendingFile(null)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const file = e.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
  }

  const handleReset = () => {
    if (canAnalyzeNew) {
      onAnalysisComplete(null)
    }
  }

  return (
    <div className="space-y-6">
      {/* Upload Zone */}
      {!currentAnalysis && canAnalyzeNew && (
        <div
          className={`
 border-2 border-dashed rounded-lg p-12 text-center
 transition-colors cursor-pointer
 ${
              dragActive
                ? 'border-app-accent bg-app-highlight'
                : 'border-app-line hover:border-app-line bg-app-subtle'
            }
            ${(uploading || analyzing) && 'opacity-50 pointer-events-none'}
          `}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => document.getElementById('resume-upload')?.click()}
        >
          <input
            id="resume-upload"
            type="file"
            accept=".pdf"
            onChange={handleChange}
            className="hidden"
          />

          {uploading ? (
            <div className="text-app-muted">
              <div className="animate-spin w-12 h-12 border-4 border-app-accent border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="font-semibold">Uploading resume...</p>
            </div>
          ) : analyzing ? (
            <div className="text-app-muted">
              <div className="animate-spin w-12 h-12 border-4 border-app-accent border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="font-semibold mb-2 transition-opacity duration-300">
                {ANALYZING_MESSAGES[analyzingMessageIndex]}
              </p>
              <p className="text-sm text-app-muted">This may take 30-60 seconds</p>
            </div>
          ) : (
            <>
              <DocumentArrowUpIcon className="h-16 w-16 text-app-muted mb-4" />
              <p className="text-app-ink font-semibold mb-2">
                Drop your resume here or click to upload
              </p>
              <p className="text-sm text-app-muted">PDF only • Max 5MB</p>
            </>
          )}
        </div>
      )}

      {!currentAnalysis && !canAnalyzeNew && (
        <UpgradePrompt type="feature" featureName="Resume Analysis" />
      )}

      {error && (
        <div className="rounded-lg border border-rose-200 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-950/30 px-4 py-3">
          <p className="font-semibold text-rose-700 dark:text-rose-200 mb-1">Error</p>
          <p className="text-sm text-rose-700 dark:text-rose-200">{error}</p>
        </div>
      )}

      {/* Analysis Results */}
      {currentAnalysis && (
        <div className="space-y-6">
          {/* Header with scores */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-app-line bg-app-surface p-6 rounded-2xl shadow-app-card">
              <p className="text-app-muted text-sm mb-2">Overall Score</p>
              <div className="flex items-end gap-2">
                <span className="text-4xl font-bold text-app-ink">
                  {currentAnalysis.overall_score}
                </span>
                <span className="text-app-muted mb-1">/100</span>
              </div>
              <div className="mt-3 h-2 bg-app-subtle rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-sky-500 transition-all duration-500"
                  style={{ width: `${currentAnalysis.overall_score}%` }}
                />
              </div>
            </div>

            <div className="border border-app-line bg-app-surface p-6 rounded-2xl shadow-app-card">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-app-muted text-sm">ATS Compatibility</p>
                <div className="group relative">
                  <svg className="w-4 h-4 text-app-muted cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-64 p-2 bg-app-surface border border-app-line rounded-lg text-xs text-app-ink z-10">
                    <strong>ATS (Applicant Tracking System)</strong> - automated systems used by companies to filter resumes. A higher score means your resume is more likely to pass automated screening.
                  </div>
                </div>
              </div>
              <div className="flex items-end gap-2">
                <span className="text-4xl font-bold text-app-ink">
                  {currentAnalysis.ats_score}
                </span>
                <span className="text-app-muted mb-1">/100</span>
              </div>
              <div className="mt-3 h-2 bg-app-subtle rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-500 to-emerald-500 transition-all duration-500"
                  style={{ width: `${currentAnalysis.ats_score}%` }}
                />
              </div>
            </div>
          </div>

          {/* Strengths */}
          <div className="border border-app-line bg-app-surface p-6 rounded-2xl shadow-app-card">
            <h3 className="text-lg font-semibold text-emerald-700 dark:text-emerald-400 mb-4 flex items-center gap-2 font-display">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Strengths
            </h3>
            <ul className="space-y-2">
              {currentAnalysis.strengths.map((strength, i) => (
                <li key={i} className="flex items-start gap-2 text-app-muted">
                  <span className="text-emerald-700 dark:text-emerald-400 mt-1 flex-shrink-0">•</span>
                  <span>{strength}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Weaknesses */}
          <div className="border border-app-line bg-app-surface p-6 rounded-2xl shadow-app-card">
            <h3 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-4 flex items-center gap-2 font-display">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              Areas for Improvement
            </h3>
            <ul className="space-y-2">
              {currentAnalysis.weaknesses.map((weakness, i) => (
                <li key={i} className="flex items-start gap-2 text-app-muted">
                  <span className="text-red-700 dark:text-red-400 mt-1 flex-shrink-0">•</span>
                  <span>{weakness}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Suggestions */}
          <div className="border border-app-line bg-app-surface p-6 rounded-2xl shadow-app-card">
            <h3 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-4 flex items-center gap-2 font-display">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
              Actionable Suggestions
            </h3>
            <ul className="space-y-3">
              {currentAnalysis.suggestions.map((suggestion, i) => (
                <li key={i} className="flex items-start gap-3 text-app-muted">
                  <span className="bg-sky-100 dark:bg-sky-500/20 text-sky-700 dark:text-sky-400 rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0 mt-0.5 font-semibold">
                    {i + 1}
                  </span>
                  <span>{suggestion}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Missing Keywords */}
          {currentAnalysis.missing_keywords && currentAnalysis.missing_keywords.length > 0 && (
            <div className="border border-app-line bg-app-surface p-6 rounded-2xl shadow-app-card">
              <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-400 mb-4 font-display">
                Missing Keywords
              </h3>
              <div className="flex flex-wrap gap-2">
                {currentAnalysis.missing_keywords.map((keyword, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-amber-500/20 text-amber-800 dark:text-amber-400 rounded-full text-sm border border-amber-500/30"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Formatting Issues */}
          {currentAnalysis.formatting_issues &&
            currentAnalysis.formatting_issues.length > 0 && (
              <div className="border border-app-line bg-app-surface p-6 rounded-2xl shadow-app-card">
                <h3 className="text-lg font-semibold text-app-ink mb-4 font-display">
                  Formatting Issues
                </h3>
                <ul className="space-y-2">
                  {currentAnalysis.formatting_issues.map((issue, i) => (
                    <li key={i} className="flex items-start gap-2 text-app-muted">
                      <span className="text-app-muted mt-1 flex-shrink-0">•</span>
                      <span>{issue}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

          {/* Action Button */}
          <button
            onClick={handleReset}
            className="w-full rounded-lg bg-app-primary py-3 text-white font-semibold hover:bg-app-primary-hover transition-colors"
          >
            Analyze Another Resume
          </button>
        </div>
      )}

      {/* Consent Modal */}
      <DataProcessingConsentModal
        isOpen={showConsentModal}
        onConsent={handleConsent}
        onDecline={handleDeclineConsent}
      />
    </div>
  )
}

